import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonStringBasics = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Text Processing</div>
                <h1>Advanced String Handling</h1>
                <p className="hero-lead">
                    Strings are the DNA of the web. In Python, they are immutable sequences of Unicode characters.
                    Mastering them means understanding memory efficiency, encoding pitfalls, and the art of O(n) manipulation.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    In "lower" languages like C, a string is just an array of bytes ending with a null character.
                    In Python 3, a string (<code>str</code>) is much more sophisticated: it is an <strong>immutable sequence of Unicode code points</strong>.
                </p>
                <p>
                    This distinction is critical. It means Python strings can handle Emoji, Japanese Kanji, and mathematical symbols right out of the box.
                    However, this power comes with strict rules about memory and performance thatif ignoredcan slow your application to a crawl.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Immutability:</strong> The "Stone Tablet" architecture of Python strings.</li>
                        <li><strong>Internals:</strong> How string interning saves RAM (and why <code>a is b</code> works for some strings).</li>
                        <li><strong>Slicing Mastery:</strong> Extracting data with <code>[start:end:step]</code>.</li>
                        <li><strong>The Performance Trap:</strong> Why <code>join()</code> beats <code>+</code> by a mile.</li>
                        <li><strong>Sanitization:</strong> Professional cleaning with <code>strip()</code>, <code>translate()</code>, and `casefold()`.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Architecture: Stone Tablets (Immutability)</h2>
                <p>
                    Python strings are <strong>Immutable</strong>. Once you create a string object, it cannot be changed.
                </p>
                <p>
                    Think of a Python string as a text carved into a <strong>Stone Tablet</strong>.
                    You cannot change a letter on the tablet. If you want to fix a typo, you must carve a completely new tablet
                    with the correction and throw the old one away (or let the Garbage Collector crush it).
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Attempting Mutation</span>
                    </div>
                    <CodeBlock language="python" code={`s = "Hello World"

#  Types like Lists are mutable (Whiteboard)
# list[0] = "J"  # Works!

#  Strings are Stone Tablets
# s[0] = "J"     # TypeError: 'str' object does not support item assignment

# The "New Tablet" Way
# Create a NEW string and move the variable label to it
s = "J" + s[1:] 
print(s)  # "Jello World"`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Why Immutable?</strong> Immutability allows strings to be <strong>Hashable</strong>, which means they can be used as
                        Dictionary keys. It also makes them thread-safe by default.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Memory Internals: String Interning</h2>
                <p>
                    If strings are immutable, does Python create a new object every time you type <code>"hello"</code>?
                    Not always. Python uses a technique called <strong>Interning</strong> (caching) for small, common strings.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The "is" Operator Surprise</span>
                    </div>
                    <CodeBlock language="python" code={`# Python automatically interns simple strings
a = "hello"
b = "hello"
print(a is b)  # True (They point to the EXACT same object in memory)

# But it doesn't intern everything...
s1 = "hello world" * 1000
s2 = "hello world" * 1000
print(s1 == s2) # True (Same value)
print(s1 is s2) # False (Different objects)

# You can FORCE interning for optimization (Advanced)
import sys
import { ProTip } from '../../components/LessonUI';
s3 = sys.intern("hello world" * 1000)
s4 = sys.intern("hello world" * 1000)
print(s3 is s4) # True (Memory saved!)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Slicing: Surgical Extraction</h2>
                <p>
                    Slicing is one of Python's most powerful features. The syntax <code>[start:end:step]</code> allows you to extract
                    or reverse substrings in O(k) time (where k is slice length).
                </p>

                <div className="analogy-box">
                    <h3> Visualizing Indices</h3>
                    <p style={{ fontFamily: 'monospace', fontSize: '1.2em', whiteSpace: 'pre', overflowX: 'auto' }}>
                        P  y  t  h  o  n <br />
                        0  1  2  3  4  5  (Positive Index)<br />
                        -6 -5 -4 -3 -2 -1  (Negative Index)
                    </p>
                </div>

                <CodeBlock language="python" code={`text = "Python Programming"

# 1. Range [Start (inclusive) : End (exclusive)]
print(text[0:6])   # "Python"
print(text[:6])    # "Python" (Implicit start 0)
print(text[7:])    # "Programming" (Implicit end)

# 2. Negative Slicing (From the end)
print(text[-1])    # "g" (Last char)
print(text[-4:])   # "ming" (Last 4 chars)

# 3. The Step Argument
print(text[::2])   # "Pto rgamn" (Every 2nd char)
print(text[::-1])  # "gnimmargorP nohtyP" (REVERSE string!)`} />
            </div>

            <div className="content-section">
                <h2>Performance: The Concatenation Trap</h2>
                <p>
                    This is a classic interview question. Why should you avoid <code>+</code> in a loop?
                </p>
                <p>
                    Remember the "Stone Tablet". Every time you do <code>s = s + "x"</code>, Python has to create a <strong>new tablet</strong>,
                    copy all the old characters, adds the new one, and destroy the old tablet.
                </p>
                <p>
                    If the string is length N, copying it takes O(N). Doing this N times in a loop leads to <strong>O(N) complexity</strong>. Uses immense CPU and RAM.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Slow vs Fast Construction</span>
                    </div>
                    <CodeBlock language="python" code={`words = ["Python", "is", "fast", "if", "used", "correctly"]

#  The Rookie Way (Quadratic Time O(N))
# Creates intermediate strings: "Pythonis", "Pythonisfast"...
s = ""
for w in words:
    s += w + " "  

# The Pro Way (Linear Time O(N))
# Calculates total size once, allocates memory, fills it.
s = " ".join(words)
print(s)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Case Study: Input Sanitization</h2>
                <p>
                    Data from users is always messy. A "Gold Standard" engineer knows how to clean it efficiently.
                </p>

                <h3>Standard Cleaning Pipeline</h3>
                <CodeBlock language="python" code={`raw_input = "   User_Name_123   "

# 1. Strip Whitespace
clean = raw_input.strip()  # "User_Name_123"

# 2. Case Normalization
# 'casefold()' is better than 'lower()' - it handles German '' -&gt; 'ss'
normalized = clean.casefold()

# 3. Checking content
if normalized.isalnum():
    print("Valid alphanumeric")
    
# 4. Replacement
print(clean.replace("_", " ")) # "User Name 123"`} />
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices & Takeaways</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>join()</code> to build strings from lists.</li>
                            <li>Use <code>casefold()</code> for robust searching/matching.</li>
                            <li>Use Slicing <code>[::-1]</code> to reverse strings.</li>
                            <li>Use <code>sys.intern()</code> only if you have millions of duplicate strings.</li>
                            <li>Use <code>f-strings</code> (next lesson!) for variable injection.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't use <code>+=</code> in large loops.</li>
                            <li>Don't rely on <code>a is b</code> for string equality (use <code>==</code>).</li>
                            <li>Don't treat strings as bytes (use <code>.encode()</code> for that).</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonStringBasics;


